import { useEffect } from 'react';
import { Box, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@commander/api-client';

interface DeactivatedScreenProps {
  code: string;
  reason?: string;
}

export function DeactivatedScreen({ code, reason }: DeactivatedScreenProps) {
  const queryClient = useQueryClient();

  // Auto-retry config fetch every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kioskConfig.byCode(code) });
    }, 30_000);

    return () => clearInterval(interval);
  }, [code, queryClient]);

  return (
    <Center h="100dvh" style={{ backgroundColor: 'var(--bg-base)' }}>
      <Stack align="center" gap={24} style={{ maxWidth: 480, padding: 'var(--space-6)' }}>
        <Box
          style={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#F59E0B"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </Box>

        <Stack align="center" gap={8}>
          <Title
            order={2}
            ta="center"
            fw={700}
            style={{
              fontFamily: 'var(--font-heading)',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              letterSpacing: '-0.01em',
            }}
          >
            Kiosk Unavailable
          </Title>
          <Text
            ta="center"
            fw={500}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-secondary)',
              fontSize: '0.9375rem',
              lineHeight: 1.6,
            }}
          >
            {reason ?? 'This kiosk has been deactivated. Please contact your administrator.'}
          </Text>
        </Stack>

        <Stack align="center" gap={8} mt={16}>
          <Loader size="xs" color="yellow" />
          <Text
            ta="center"
            fw={500}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-muted)',
              fontSize: '0.8125rem',
            }}
          >
            Checking for reactivation...
          </Text>
        </Stack>

        <Text
          mt={8}
          fw={600}
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--text-muted)',
            fontSize: '0.6875rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Commander Equipment Kiosk
        </Text>
      </Stack>
    </Center>
  );
}
